<template>
  <SimpleTable card-wrap data-cy="email-list-card">
    <thead>
      <tr>
        <th>
          <div class="d-flex align-items-center">
            <i class="fa-regular fa-envelope mr-2"></i>
            <span class="info-header text-sm font-bold mr-2">Email</span>
            <div class="number-badge">{{ filteredEmails.length }}</div>
            <ExpandButton
              v-if="alternateEmailsExist"
              class="sera-ml-auto"
              title=""
              :expanded="showAdditionalEmails"
              @toggle-expanded="toggleShowAdditionalEmails"
            />
          </div>
        </th>
      </tr>
    </thead>
    <template v-for="(email, i) in filteredEmails" :key="email.id">
      <tr
        v-if="i === 0"
        :key="`${email.id}-primary-header`"
        class="list-item-header font-bold"
      >
        <td><TruncateText>Primary Email</TruncateText></td>
      </tr>
      <tr
        v-if="i === 0 && !email.primary"
        :key="`${email.id}-missing-primary-placeholder`"
        class="list-item"
      >
        <td>--</td>
      </tr>
      <tr
        v-if="
          showAdditionalItemHeader(showAdditionalEmails, primaryEmailExists, i)
        "
        :key="`${email.id}-additional-header`"
        class="list-item-header font-bold"
      >
        <td><TruncateText>Additional Email(s)</TruncateText></td>
      </tr>
      <tr
        class="list-item"
        :class="{
          'list-item-collapsed': !email.primary && !showAdditionalEmails
        }"
      >
        <td class="info">
          <div class="w-full">
            <TruncateText>
              {{ email.email_address }}
            </TruncateText>
          </div>
          <div v-if="email.label" class="w-full">
            <TruncateText class="label-text">
              {{ email.label }}
            </TruncateText>
          </div>
        </td>
      </tr>
    </template>
    <tr v-if="!filteredEmails.length">
      <td
        class="text-danger cursor-pointer !whitespace-nowrap"
        @click="emit('edit-profile')"
      >
        <i class="fas fa-exclamation-circle" />
        Missing Email Address
      </td>
    </tr>
  </SimpleTable>
</template>

<script setup lang="ts">
import { computed, ref, withDefaults } from "vue";

import ExpandButton from "@/components/ExpandButton.vue";
import SimpleTable from "@/components/SimpleTable.vue";
import TruncateText from "@/components/TruncateText.vue";
import { CustomerEmailsEntity } from "@/typings/customer";

const emit = defineEmits(["edit-profile"]);
const props = withDefaults(
  defineProps<{
    emails: CustomerEmailsEntity[];
  }>(),
  {
    emails: () => []
  }
);
const showAdditionalEmails = ref(false);
const filteredEmails = computed(() =>
  props.emails.filter((email) => email.email_address)
);

const primaryEmailExists = computed(() =>
  filteredEmails.value.some((email) => email.primary)
);

const alternateEmailsExist = computed(() =>
  filteredEmails.value.some((email) => !email.primary)
);

const toggleShowAdditionalEmails = () => {
  showAdditionalEmails.value = !showAdditionalEmails.value;
};

const showAdditionalItemHeader = (toggleShow, primaryExists, index) => {
  if (!toggleShow) return false;

  switch (index) {
    case 0:
      return !primaryExists;
    case 1:
      return primaryExists;
    default:
      return false;
  }
};
</script>

<style lang="scss" scoped>
.label-text {
  white-space: nowrap;
  color: var(--sera-color-core-blue-cobalt-blue-800);
}

.list-item {
  td {
    white-space: nowrap;
  }

  &.list-item-collapsed {
    visibility: collapse;
  }
}

.info {
  color: #5d5d5d;
}

.info-header {
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
}

.info-icon {
  width: 50px;
  text-align: center;
}

.number-badge {
  display: flex;
  height: 18px;
  padding: 0 4px;
  line-height: 18px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid var(--sera-color-neutral-gray-med-gray-500);
  background: var(--sera-color-neutral-gray-light-gray-100);
  font-size: 12px;
  font-weight: 600;
}
</style>
